/**
 * Translated ABC introduction page body.
 *
 * Mirrors the English CMS HTML for page_id "abc-introduction"
 * (classes, links, and the embedded stylesheet stay identical).
 */

const PAGE_STYLE = `
<style>
.abc-intro-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  line-height: 1.8;
  color: var(--color-text-primary, #333);
}

.abc-intro-page h1 {
  color: var(--color-primary, #60a5fa);
  font-size: 2.5em;
  margin-bottom: 20px;
  border-bottom: 2px solid var(--color-primary, #60a5fa);
  padding-bottom: 10px;
}

.abc-intro-page h2 {
  color: var(--color-primary, #60a5fa);
  font-size: 1.8em;
  margin-top: 30px;
  margin-bottom: 15px;
}

.abc-intro-page h3 {
  color: var(--color-text-primary, #333);
  font-size: 1.4em;
  margin-top: 20px;
  margin-bottom: 10px;
}

.abc-intro-page p {
  margin-bottom: 15px;
}

.abc-intro-page ul,
.abc-intro-page ol {
  margin-left: 30px;
  margin-bottom: 20px;
}

.abc-intro-page li {
  margin-bottom: 8px;
}

.abc-intro-page a {
  color: var(--color-primary, #60a5fa);
  text-decoration: none;
  font-weight: 600;
}

.abc-intro-page a:hover {
  text-decoration: underline;
}

.abc-intro-page strong {
  color: var(--color-text-primary, #333);
}
</style>
`;

const frenchAbc = (d) => `
<div class="abc-intro-page">
  <h1>${d.title}</h1>

  <p>Quand on cherche un air sur le Web, on le trouve souvent en notation ABC. C'est parce que les ABC sont du texte, ce qui permet aux moteurs de recherche comme Google de les trouver. Les ABC se convertissent facilement en notation musicale classique. Cette introduction vise à aider les débutants à tirer parti de cette ressource.</p>

  <p>Notre site publie des centaines d'airs au format ABC. Plus important encore, de nombreux recueils classiques comme <em>The Athole Collection</em> (consultez le site Fiddler's Companion d'Andrew Kuntz) ont été mis en ligne ailleurs. Dans le monde irlandais, des centaines de sites publient des airs en ABC. Il faut connaître le format pour accéder à cette musique.</p>

  <h2>Utiliser nos outils ABC</h2>

  <p>Nous proposons un <a href="/abc-tool">outil ABC</a> complet qui vous permet de :</p>
  <ul>
    <li>Convertir des fichiers MIDI en notation ABC</li>
    <li>Charger et afficher des fichiers ABC</li>
    <li>Écouter des airs avec un tempo et des instruments réglables</li>
    <li>Télécharger la partition en PDF, PNG ou MIDI</li>
    <li>Modifier et mettre à jour la notation ABC en temps réel</li>
  </ul>

  <p>Vous pouvez ouvrir notre <a href="/abc-tool">outil ABC</a> directement, ou parcourir notre vaste collection d'<a href="/tunes?type=abc">airs ABC</a> sur la page des airs.</p>

  <h2>Conversion simple dans le navigateur</h2>

  <p>Un programme ABC offre plus de fonctions, mais un air ABC bien formé peut aussi être converti directement dans votre navigateur. Il suffit de copier-coller les ABC d'un seul air dans notre <a href="/abc-tool">outil ABC en ligne</a>. Envoyez le formulaire et, quelques instants plus tard, vous obtenez une partition en notation classique, avec lecture MIDI en option. C'est aussi simple que cela.</p>

  <p>Pour cette fonction de base, vous n'avez rien à télécharger. Il vous faut seulement un accès Web et un navigateur.</p>

  <h3>Comment utiliser notre outil ABC</h3>

  <ol>
    <li>Ouvrez notre <a href="/abc-tool">outil ABC</a></li>
    <li>Téléversez un fichier MIDI ou collez de la notation ABC</li>
    <li>Affichez la notation classique</li>
    <li>Écoutez l'air, ajustez le tempo ou téléchargez-le dans différents formats</li>
  </ol>

  <p><strong>NOTE TECHNIQUE</strong> — Certains airs ABC trouvés ailleurs sur le Web sont publiés sur des pages HTML plutôt que sur des pages de texte brut (The Fiddler's Companion). Si vous copiez des ABC depuis une page HTML, le collage dans un programme ABC ou un convertisseur ajoute des sauts de ligne supplémentaires qu'il faut retirer pour que la conversion réussisse. Avec notre <a href="/abc-tool">outil ABC</a>, ABC Navigator, ABC2win, etc., ces problèmes disparaissent.</p>

  <p>Si vous comptez seulement utiliser les ABC que d'autres publient ou vous ${d.sendVerb}, vous avez maintenant tous les outils et l'expérience nécessaires. Si vous voulez écrire et modifier des airs, poursuivez la lecture...</p>

  <h2>Pourquoi la notation ABC ?</h2>

  <p>Avec l'aide de votre ordinateur, les ABC font le pont entre l'apprentissage « à l'oreille » et « à l'œil ». On peut taper les airs comme du texte et les lire sans programme. Les fichiers ABC sont compatibles d'une plateforme à l'autre et occupent peu de mémoire. Pour ${d.sendNoun} des airs, les ABC sont idéaux. Interprétés par un ${d.shareware}, ils fournissent tout ce qu'il faut pour la lecture et l'affichage de la notation classique à l'écran.</p>

  <p>Pour les enseignants, les ABC peuvent servir de tremplin vers la notation classique. Apprendre à écrire des ABC aide chacun à mieux comprendre les rudiments de la musique.</p>

  <h2>À propos de la notation ABC</h2>

  <p>Les ABC ont été inventés par Chris Walshaw comme un moyen simple de transcrire la musique traditionnelle. Tout en évoluant dans des pays aussi éloignés que le Royaume-Uni, l'Allemagne, la Suède, le Canada et les États-Unis, M. Walshaw et la communauté ABC travaillent à standardiser le langage. Aujourd'hui, on l'utilise surtout pour noter et jouer des airs traditionnels comme les gigues et les reels, mais on s'en est déjà servi pour transcrire aussi bien le koto japonais que Scarlatti.</p>

  <p>Si les choses avancent comme elles le devraient, d'ici une cinquantaine d'années, ce langage permettra d'accéder à toute la musique de la société occidentale avec les moteurs de recherche du quotidien. En attendant, chaque partition importante doit être soigneusement traduite en ABC et mise en ligne.</p>

  <h2>Explorer notre collection</h2>

  <p>Parcourez notre vaste collection d'<a href="/tunes?type=abc">airs ABC</a>, notamment de la musique traditionnelle de violon du Cap-Breton, d'Écosse et d'Irlande. Vous pouvez chercher par titre, compositeur, tonalité, rythme ou région.</p>

  <h2>Ressources</h2>

  <ul>
    <li><a href="/abc-tool">Outil ABC</a> — convertir du MIDI en ABC, afficher la notation et jouer des airs</li>
    <li><a href="/tunes?type=abc">Parcourir les airs</a> — chercher et explorer notre collection d'airs ABC</li>
    <li><a href="/page/abc-programs">${d.programsLink}</a> — programmes de bureau et outils en ligne</li>
    <li><a href="http://www.mandolintab.net/abcconverter.php" target="_blank" rel="noopener noreferrer">Convertisseur ABC Mandolintab.net</a> — convertisseur en ligne de rechange</li>
    <li><a href="http://www.abcnotation.com" target="_blank" rel="noopener noreferrer">Site ABC Notation</a> — ressource officielle sur la notation ABC</li>
  </ul>
</div>
${PAGE_STYLE}
`;

const germanAbc = `
<div class="abc-intro-page">
  <h1>Einführung in ABC-Notation</h1>

  <p>Wer im Web nach einem Stück sucht, findet es oft in ABC-Notation. Das liegt daran, dass ABCs Text sind und Suchmaschinen wie Google sie dadurch finden können. ABCs lassen sich leicht in klassische Notenschrift umwandeln. Diese Einführung soll Anfängern helfen, diese Ressource zu nutzen.</p>

  <p>Unsere Website veröffentlicht Hunderte von Stücken im ABC-Format. Noch wichtiger: Viele klassische Tunebooks wie <em>The Athole Collection</em> (siehe Andrew Kuntz’ Fiddler's Companion) sind auf anderen Seiten online. In der irischen Musikszene gibt es Hunderte von ABC-Seiten. Wer diese Musik nutzen will, muss das Format kennen.</p>

  <h2>Unsere ABC-Werkzeuge nutzen</h2>

  <p>Wir bieten ein umfassendes <a href="/abc-tool">ABC-Werkzeug</a>, mit dem Sie:</p>
  <ul>
    <li>MIDI-Dateien in ABC-Notation umwandeln</li>
    <li>ABC-Dateien laden und anzeigen</li>
    <li>Stücke mit einstellbarem Tempo und Instrumenten abspielen</li>
    <li>Notenschrift als PDF, PNG oder MIDI herunterladen</li>
    <li>ABC-Notation in Echtzeit bearbeiten und aktualisieren</li>
  </ul>

  <p>Sie können unser <a href="/abc-tool">ABC-Werkzeug</a> direkt öffnen oder unsere umfangreiche Sammlung von <a href="/tunes?type=abc">ABC-Stücken</a> auf der Tunes-Seite durchsuchen.</p>

  <h2>Einfache Umwandlung im Browser</h2>

  <p>Ein ABC-Programm bietet mehr Funktionen, aber korrekt formatierte ABC-Stücke lassen sich auch direkt im Browser umwandeln. Kopieren Sie einfach die ABCs eines einzelnen Stücks und fügen Sie sie in unser <a href="/abc-tool">Online-ABC-Werkzeug</a> ein. Senden Sie das Formular ab — kurz darauf liegt die formatierte Notenschrift vor, optional mit MIDI-Wiedergabe. So einfach ist das.</p>

  <p>Für diese einfache Funktion brauchen Sie kein Programm herunterzuladen. Es reicht ein Internetzugang und ein Browser.</p>

  <h3>So nutzen Sie unser ABC-Werkzeug</h3>

  <ol>
    <li>Öffnen Sie unser <a href="/abc-tool">ABC-Werkzeug</a></li>
    <li>Laden Sie eine MIDI-Datei hoch oder fügen Sie ABC-Notation ein</li>
    <li>Sehen Sie sich die klassische Notenschrift an</li>
    <li>Spielen Sie das Stück ab, ändern Sie das Tempo oder laden Sie es in verschiedenen Formaten herunter</li>
  </ol>

  <p><strong>HINWEIS</strong> — Manche ABC-Stücke im Netz stehen auf HTML-Seiten statt auf reinen Textseiten (The Fiddler's Companion). Kopiert man ABCs von einer HTML-Seite, entstehen beim Einfügen in ein ABC-Programm oder ein Umwandlungswerkzeug zusätzliche Zeilenumbrüche, die vor einer erfolgreichen Übersetzung entfernt werden müssen. Mit unserem <a href="/abc-tool">ABC-Werkzeug</a>, ABC Navigator, ABC2win usw. entfallen diese Probleme.</p>

  <p>Wenn Sie nur ABCs nutzen wollen, die andere veröffentlichen oder Ihnen schicken, haben Sie jetzt alle nötigen Werkzeuge und Erfahrungen. Wenn Sie selbst Stücke schreiben und bearbeiten möchten, lesen Sie weiter...</p>

  <h2>Warum ABC-Notation?</h2>

  <p>Mit dem Computer können ABCs eine Brücke zwischen dem Lernen „nach Gehör“ und „nach Noten“ schlagen. Stücke lassen sich als Text tippen und ohne Programm lesen. ABC-Dateien sind plattformübergreifend und speichersparend. Zum Versenden von Stücken per E-Mail sind ABCs ideal. Mit einem Shareware-Programm liefern sie alle Informationen für Wiedergabe und klassische Notenschrift am Bildschirm.</p>

  <p>Für Musiklehrerinnen und -lehrer können ABCs ein Zwischenschritt zur klassischen Notation sein. Wer ABCs schreiben lernt, versteht die Grundlagen der Musik besser.</p>

  <h2>Über ABC-Notation</h2>

  <p>ABCs wurden von Chris Walshaw als einfache Art erfunden, Volksmusik zu transkribieren. Während sich die Notation in Ländern wie Großbritannien, Deutschland, Schweden, Kanada und den USA weiterentwickelt, arbeiten Herr Walshaw und die ABC-Gemeinschaft an einer Standardisierung der Sprache. Derzeit dient sie vor allem zum Notieren und Abspielen traditioneller Stücke wie Jigs und Reels, wurde aber schon für alles von japanischem Koto bis Scarlatti verwendet.</p>

  <p>Wenn es so weitergeht, wird diese Sprache in etwa 50 Jahren die gesamte Musik der westlichen Welt über alltägliche Suchmaschinen zugänglich machen. Bis dahin muss jedes wichtige Notenstück sorgfältig in ABCs übersetzt und online gestellt werden.</p>

  <h2>Unsere Sammlung entdecken</h2>

  <p>Durchsuchen Sie unsere umfangreiche Sammlung von <a href="/tunes?type=abc">ABC-Stücken</a>, darunter traditionelle Fiedelmusik aus Cape Breton, Schottland und Irland. Sie können nach Titel, Komponist, Tonart, Rhythmus oder Region suchen.</p>

  <h2>Ressourcen</h2>

  <ul>
    <li><a href="/abc-tool">ABC-Werkzeug</a> — MIDI in ABC umwandeln, Notation anzeigen und Stücke abspielen</li>
    <li><a href="/tunes?type=abc">Stücke durchsuchen</a> — unsere ABC-Sammlung suchen und erkunden</li>
    <li><a href="/page/abc-programs">ABC-Programme und Umwandlungswebsites nutzen</a> — Desktop-Programme und Online-Werkzeuge</li>
    <li><a href="http://www.mandolintab.net/abcconverter.php" target="_blank" rel="noopener noreferrer">Mandolintab.net ABC-Konverter</a> — alternatives Online-Umwandlungswerkzeug</li>
    <li><a href="http://www.abcnotation.com" target="_blank" rel="noopener noreferrer">ABC-Notation-Website</a> — offizielle Ressource zur ABC-Notation</li>
  </ul>
</div>
${PAGE_STYLE}
`;

export const ABC_INTRODUCTION_TRANSLATIONS = {
  'fr-CA': {
    title: 'Introduction aux ABC',
    description:
      'Découvrez la notation ABC, un format texte pour la musique traditionnelle. Convertissez des fichiers MIDI en ABC, parcourez notre collection et utilisez nos outils en ligne gratuits.',
    html: frenchAbc({
      title: 'Introduction aux ABC',
      sendVerb: 'envoyez par courriel',
      sendNoun: 'envoyer par courriel',
      shareware: 'graticiel',
      programsLink: 'Utiliser les programmes ABC et les sites de conversion',
    }),
  },
  'fr-FR': {
    title: 'Introduction aux ABC',
    description:
      'Découvrez la notation ABC, un format texte pour la musique traditionnelle. Convertissez des fichiers MIDI en ABC, parcourez notre collection et utilisez nos outils en ligne gratuits.',
    html: frenchAbc({
      title: 'Introduction aux ABC',
      sendVerb: 'envoyez par e-mail',
      sendNoun: "l'envoi par e-mail",
      shareware: 'partagiciel',
      programsLink: 'Utiliser les programmes ABC et les sites de conversion',
    }),
  },
  de: {
    title: 'Einführung in ABC-Notation',
    description:
      'ABC-Notation: ein textbasiertes Format für traditionelle Musik. MIDI-Dateien in ABC umwandeln, unsere Sammlung durchsuchen und kostenlose Online-Werkzeuge nutzen.',
    html: germanAbc,
  },
};
